<template>
    <snippet-card :modelValue="props.modelValue">
        <template v-if="!FEATURES.contact_groups || !belongsToCorporate || (props.groupId && props.modelValue.is_locked)" v-slot:actions>
            <div v-if="!FEATURES.contact_groups || !belongsToCorporate" class="flex flex-row gap-2 w-full sm:w-auto">
                <vx-dropdown placement="right">
                    <vx-button data-test="rule-dropdown"
                        aria-label="Snippet actions"
                        type="button"
                        color="muted"
                        outline
                        class="w-[45px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <vx-dropdown-item data-test="delete"
                            @click="deleteSnippet()">
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>

                <router-button v-if="props.templateView"
                    data-test="edit-button"
                    :to="{ name: 'snippets.templates.edit', params: { snippetId: props.modelValue.id } }"
                    color="muted"
                    class="w-full sm:w-auto"
                    outline>
                    Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                </router-button>
                <vx-button v-else
                    @click.prevent="emit('edit', props.modelValue)"
                    :data-test="`edit-btn-${props.modelValue.id}`"
                    color="muted"
                    class="w-full sm:w-auto"
                    outline>
                    Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                </vx-button>
            </div>
            <div v-else-if="props.groupId && props.modelValue.is_locked" class="w-full sm:w-auto">
                <router-button
                    :to="{ name: 'quick-blast', query: { snippet_id: props.modelValue.id } }"
                    :data-test="`send-${props.modelValue.id}`"
                    color="muted"
                    class="w-full"
                    outline>
                    Send <font-awesome-icon widthAuto :icon="faRocket" />
                </router-button>
            </div>
        </template>
        <template v-if="props.templateView && FEATURES.contact_groups" v-slot:details>
            <div class="text-sm flex flex-col sm:flex-row sm:items-center">
                <span class="text-slate-500 font-extrabold sm:font-normal">Groups: </span>
                <span class="sm:ml-3" data-test="groups-count">
                    <font-awesome-icon v-if="groupCounts[props.modelValue.id] === undefined"
                        widthAuto
                        :icon="faLoader"
                        spin></font-awesome-icon>
                    <meta-badge v-else :icon="faGrid2Plus" color="slate">
                        {{ groupCounts[props.modelValue.id] }}
                    </meta-badge>
                </span>
            </div>
        </template>
    </snippet-card>
</template>

<script setup>
import { faEllipsis, faFilePen, faGrid2Plus, faLoader, faRocket, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxDropdown, VxDropdownItem, VxSwal } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, inject } from 'vue';
import { snippetsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';
import MetaBadge from '~/components/general/MetaBadge.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import SnippetCard from '~/components/snippets/components/SnippetCard.vue';
import { useSnippetsGroupsCount } from '~/composables/useSnippetsGroupsCount';

const FEATURES = inject('FEATURES');

const props = defineProps({
    orgUnitId: Number,
    groupId: String,
    modelValue: {
        type: Object,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    templateView: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['deleted', 'edit']);

const { groupCounts } = useSnippetsGroupsCount();

const belongsToCorporate = computed(() => props.modelValue.is_template && (props.groupId || props.orgUnitId));

const deleteSnippet = () => {
    VxSwal.confirmation(
        'Delete Snippet',
        'Are you sure? There is no undo.',
        'danger',
        () => snippetsClient.deleteSnippet({
            teamId: props.modelValue.team_id,
            groupId: props.modelValue.group_id,
            orgUnitId: props.modelValue.org_unit_id,
        }, props.modelValue.id)
            .then(() => emit('deleted', props.modelValue.id))
            .catch(throwSwalRequestError),
    );
};
</script>
